import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Code, Cloud, Lightbulb, Server, Shield, 
  MapPin, Building2, Users, Rocket, CheckCircle2,
  ArrowRight, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const lasVegasServices = [
  {
    title: 'Las Vegas SaaS Development',
    description: 'We build custom SaaS platforms for Las Vegas and Nevada businesses. From MVP to enterprise-scale applications, our team delivers cloud-native solutions that grow with your business.',
    icon: Code,
    features: [
      'Custom SaaS platform development',
      'Multi-tenant architecture',
      'Subscription & billing integration',
      'API development & documentation',
      'Mobile-responsive applications'
    ]
  },
  {
    title: 'Las Vegas Web Development',
    description: 'Professional web development services for Las Vegas companies. We create fast, secure, and scalable web applications using modern technologies.',
    icon: Server,
    features: [
      'React, Vue, Angular frontends',
      'Node.js, Python, .NET backends',
      'E-commerce solutions',
      'Progressive Web Apps (PWA)',
      'Website performance optimization'
    ]
  },
  {
    title: 'Nevada Cloud Services',
    description: 'Cloud infrastructure and DevOps services for Nevada businesses. We help you migrate, optimize, and manage your cloud environment on AWS, Azure, or GCP.',
    icon: Cloud,
    features: [
      'Cloud migration & architecture',
      'AWS, Azure, GCP expertise',
      'CI/CD pipeline setup',
      'Infrastructure as Code',
      'Cost optimization & monitoring'
    ]
  },
  {
    title: 'Las Vegas IT Consulting',
    description: 'Strategic IT consulting for Las Vegas businesses. We provide technical guidance to help you make informed technology decisions and accelerate growth.',
    icon: Lightbulb,
    features: [
      'Technical strategy & roadmapping',
      'Technology stack evaluation',
      'Security assessments',
      'Digital transformation',
      'Vendor & tool selection'
    ]
  }
];

const areasServed = [
  'Las Vegas',
  'Henderson',
  'North Las Vegas',
  'Summerlin',
  'Paradise',
  'Spring Valley',
  'Enterprise',
  'Boulder City',
  'Mesquite',
  'All of Clark County'
];

const LasVegasServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Local Business Schema for Las Vegas
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Fearless IT - Las Vegas Software Development",
    "description": "Las Vegas-based software development company specializing in SaaS development, web development, cloud services, and IT consulting for Nevada businesses.",
    "url": "https://fearlessit.io/las-vegas",
    "telephone": "",
    "email": "support@fearlessit.io",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Las Vegas",
      "addressRegion": "NV",
      "postalCode": "89101",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 36.1699,
      "longitude": -115.1398
    },
    "areaServed": areasServed.map(area => ({
      "@type": "City",
      "name": area
    })),
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 36.1699,
        "longitude": -115.1398
      },
      "geoRadius": "50 mi"
    },
    "priceRange": "$$",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Las Vegas SaaS Development & IT Services"
        description="Fearless IT is Las Vegas's premier software development company. We provide custom SaaS development, web development, cloud services, and IT consulting for Nevada businesses. Serving Las Vegas, Henderson, North Las Vegas, Summerlin, and all of Clark County."
        keywords="Las Vegas SaaS development, Las Vegas web development, Las Vegas IT services, Nevada SaaS developer, Las Vegas custom software, Nevada tech, Henderson software development, North Las Vegas IT services, Summerlin software company, Las Vegas software company, Nevada cloud services, Las Vegas app development, Clark County IT services"
        url="/las-vegas"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/059f1f9acaf3fc5385114bf3bd285e5c86bbc8b7c8f0c34d38b81fa8b6de6272.png"
        isLocalPage={true}
        structuredData={localBusinessSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-24 sm:pb-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              <MapPin className="h-4 w-4" />
              Serving Las Vegas & All of Nevada
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Las Vegas <span className="text-blue-600">SaaS Development</span> & IT Services
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Your local Las Vegas technology partner for custom software development, SaaS platforms, 
              web applications, and IT consulting. We help Nevada businesses build and scale modern technology solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
                  Get a Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/our-services">
                <Button variant="outline" className="w-full sm:w-auto px-8 py-6 text-lg border-2">
                  View All Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium">Nevada Registered Business</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-500" />
              <span className="text-sm font-medium">Las Vegas Based</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-500" />
              <span className="text-sm font-medium">Local Team</span>
            </div>
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-orange-500" />
              <span className="text-sm font-medium">Fast Turnaround</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Software Development Services in Las Vegas
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive technology services tailored for Nevada businesses
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {lasVegasServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div 
                  key={index}
                  className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:border-blue-200 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="h-7 w-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Areas We Serve in Nevada
            </h2>
            <p className="text-lg text-gray-600">
              Providing IT services and software development across the Las Vegas Valley and beyond
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {areasServed.map((area, index) => (
              <span 
                key={index}
                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-gray-700 font-medium hover:border-blue-300 hover:bg-blue-50 transition-colors"
              >
                <MapPin className="inline-block h-4 w-4 mr-1 text-blue-500" />
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Local */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose a Las Vegas Software Development Company?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Local Presence</h3>
                    <p className="text-gray-600">Meet face-to-face with our Las Vegas team. We understand the Nevada business landscape and local market needs.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Same Timezone Support</h3>
                    <p className="text-gray-600">Real-time communication during Pacific business hours. No overseas delays or timezone confusion.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Nevada Business Understanding</h3>
                    <p className="text-gray-600">We know the unique challenges and opportunities facing Nevada businesses, from hospitality to tech startups.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Rocket className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Quick Response Times</h3>
                    <p className="text-gray-600">Rapid response and fast deployment. Local presence means faster iteration and problem-solving.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Ready to Start Your Project?</h3>
              <p className="text-blue-100 mb-6">
                Get in touch with our Las Vegas team today for a free consultation. 
                We'll discuss your project requirements and provide a custom solution tailored to your business.
              </p>
              <div className="space-y-4">
                <a href="mailto:support@fearlessit.io" className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors">
                  <Mail className="h-5 w-5" />
                  <span>support@fearlessit.io</span>
                </a>
              </div>
              <Link to="/contact">
                <Button className="w-full mt-6 bg-white text-blue-600 hover:bg-blue-50">
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Let's Build Something Great Together
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Partner with Las Vegas's trusted software development company. 
            From startups to enterprises, we help Nevada businesses succeed with technology.
          </p>
          <Link to="/contact">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg">
              Start Your Project Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default LasVegasServicesPage;
